import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fcfa } from '../lib/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Radio, Users, ArrowRight, ShieldCheck, Smartphone, Truck, Store, PlayCircle } from 'lucide-react';
import { CATEGORIES, catLabel } from '../lib/categories';
import { SearchBar } from '../components/SearchBar';
import { TopSellers } from '../components/TopSellers';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { useAuth } from '../lib/auth';

const FEATURES = [
  { img: '/img/b.jpg', eyebrow: 'Vendre en direct', title: 'Présentez vos articles en live, vendez en temps réel', text: 'Lancez une diffusion depuis votre téléphone, montrez vos pièces, répondez à vos clientes et laissez les enchères faire monter les prix. Chaque live est aussi une vitrine pour votre boutique.', cta: 'Ouvrir ma boutique', to: '/register', testid: 'feature-live' },
  { img: '/img/c.jpg', eyebrow: 'Votre boutique en ligne', title: 'Une boutique professionnelle, prête en quelques minutes', text: 'Nom, logo, bannière, horaires, zones et frais de livraison, position sur la carte. Ajoutez vos produits avec plusieurs photos et un stock. Vos clientes commandent, vous confirmez le paiement Wave ou Orange Money, vous livrez.', cta: 'Découvrir la boutique', to: '/shop', testid: 'feature-shop', reverse: true },
];
const TRUST = [
  { icon: Smartphone, title: 'Paiement Wave et Orange Money', text: 'Directement au vendeur, sans carte bancaire.' },
  { icon: Truck, title: 'Livraison suivie en temps réel', text: 'Position du livreur sur la carte jusqu à votre porte.' },
  { icon: ShieldCheck, title: 'Vendeurs vérifiés', text: 'Chaque boutique est validée avant de vendre. Avis clients certifiés.' },
  { icon: Store, title: 'Inscription gratuite, sans abonnement', text: 'Ouvrez votre boutique en quelques minutes et commencez à vendre dès aujourd hui.' },
];

export default function Home() {
  const { user } = useAuth();
  const [lives, setLives] = useState([]);
  const [cat, setCat] = useState('');
  const [searchLives, setSearchLives] = useState(null);
  const shown = searchLives ?? lives;

  useEffect(() => {
    let alive = true;
    const load = async () => { try { const { data } = await api.get('/lives', { params: cat ? { category: cat } : {} }); if (alive) setLives(data); } catch {} };
    load(); const id = setInterval(load, 5000);
    return () => { alive = false; clearInterval(id); };
  }, [cat]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 pt-4">
        <SearchBar category={cat} onLivesChange={setSearchLives} compact />
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 pb-10 md:pt-10 md:pb-14 grid lg:grid-cols-[0.95fr_1.05fr] gap-8 lg:gap-12 items-center">
          <div className="reveal">
            <div className="eyebrow">Marketplace et ventes en direct au Sénégal</div>
            <h1 className="mt-3 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">La mode africaine <span className="gold-text">se vend en direct.</span></h1>
            <p className="mt-4 text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed">Achetez des pièces uniques en boutique ou en live, payez avec Wave ou Orange Money et suivez votre livraison en temps réel. Vendeuses et créateurs : ouvrez votre boutique et vendez en quelques minutes.</p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link to={user ? (user.role === 'seller' ? '/seller' : '/shop') : '/register'} data-testid="hero-cta-register" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full btn-gold font-display font-bold text-base">{user ? (user.role === 'seller' ? 'Mon espace vendeur' : 'Voir la boutique') : 'Créer mon compte gratuitement'}<ArrowRight className="h-4 w-4" /></Link>
              <Link to="/shop" data-testid="hero-cta-shop" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full btn-ghost-gold font-bold text-base">Explorer la boutique</Link>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-400">
              <Link to="/comment-ca-marche" data-testid="how-it-works-link" className="inline-flex items-center gap-1.5 text-[#F5D98B] hover:text-white underline underline-offset-4 decoration-[#E8B84A]/50"><PlayCircle className="h-4 w-4" />Comment ça marche</Link>
              <span className="inline-flex items-center gap-1.5"><span className={`h-2 w-2 rounded-full ${lives.length ? 'bg-red-500 animate-pulse' : 'bg-slate-600'}`} /><span data-testid="hero-live-count">{lives.length} live{lives.length > 1 ? 's' : ''} en ce moment</span></span>
              <span>Inscription gratuite</span>
            </div>
          </div>
          <div className="relative reveal">
            <div className="aspect-[16/10] rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl shadow-black/60">
              <img src="/img/a.jpg" alt="Collection JOTALY" className="w-full h-full object-cover" data-testid="hero-image" />
            </div>
            <div className="absolute bottom-4 left-4 right-4 glass rounded-2xl px-4 py-3 flex items-center justify-between gap-3">
              <div><div className="eyebrow">Collection</div><div className="font-display font-bold">Habits, chaussures, baskets, téléphones, tenues sénégalaises et autres</div></div>
              <Link to="/shop" className="text-xs font-bold text-[#F5D98B] whitespace-nowrap hover:text-white">Voir <ArrowRight className="inline h-3 w-3" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="max-w-7xl mx-auto w-full px-4 sm:px-6 pb-10" data-testid="trust">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {TRUST.map(({ icon: Icon, title, text }) => (
            <div key={title} className="glass rounded-2xl p-4 flex gap-3">
              <span className="h-10 w-10 shrink-0 rounded-xl bg-[#E8B84A]/10 ring-1 ring-[#E8B84A]/30 grid place-items-center"><Icon className="h-5 w-5 text-[#F5D98B]" /></span>
              <div><div className="font-bold text-sm leading-snug">{title}</div><div className="text-xs text-slate-400 mt-0.5 leading-snug">{text}</div></div>
            </div>
          ))}
        </div>
      </section>

      {/* Features with the two photos */}
      <section className="max-w-7xl mx-auto w-full px-4 sm:px-6 pb-10 space-y-8" data-testid="features">
        {FEATURES.map((f) => (
          <div key={f.title} data-testid={f.testid} className={`grid lg:grid-cols-2 gap-6 lg:gap-10 items-center ${f.reverse ? 'lg:[&>*:first-child]:order-2' : ''}`}>
            <div className="aspect-[16/10] rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-xl shadow-black/50"><img src={f.img} alt={f.title} className="w-full h-full object-cover" /></div>
            <div>
              <div className="eyebrow">{f.eyebrow}</div>
              <h2 className="mt-2 font-display font-bold text-2xl sm:text-3xl leading-tight">{f.title}</h2>
              <p className="mt-3 text-slate-300 text-sm sm:text-base leading-relaxed">{f.text}</p>
              <Link to={f.to} className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#F5D98B] hover:text-white">{f.cta}<ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        ))}
      </section>

      <TopSellers />

      {/* Lives */}
      {shown.length > 0 && (
        <section id="lives-grid" className="max-w-7xl mx-auto w-full px-4 sm:px-6 pb-16" data-testid="lives-section">
          <div className="flex items-end justify-between gap-3 mb-4">
            <div><div className="eyebrow">En direct</div><h2 className="font-display text-2xl sm:text-3xl font-bold">Lives en cours</h2></div>
            <div className="flex flex-wrap justify-end gap-1.5" data-testid="category-filter">
              {[{ id: '', label: 'Tout' }, ...CATEGORIES.filter((c) => c.id !== 'autre')].slice(0, 7).map((c) => (
                <button key={c.id} data-testid={`cat-${c.id || 'all'}`} onClick={() => setCat(c.id)} className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${cat === c.id ? 'btn-gold border-transparent' : 'border-white/10 text-slate-300 hover:border-[#E8B84A]/50'}`}>{c.label}</button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {shown.map((l) => (
              <Link to={`/live/${l.live_id}`} key={l.live_id} data-testid={`live-card-${l.live_id}`} className="group glass rounded-2xl overflow-hidden hover:-translate-y-0.5 transition-transform reveal">
                <div className="relative aspect-video overflow-hidden bg-[#0b1120]">
                  {l.thumbnail ? <img src={l.thumbnail} alt={l.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" /> : <div className="w-full h-full grid place-items-center"><Radio className="h-8 w-8 text-slate-700" /></div>}
                  <div className="absolute top-3 left-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-red-600 text-white text-[11px] font-bold"><span className="live-dot" />EN DIRECT</div>
                  <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-950/70 backdrop-blur text-white text-xs"><Users className="h-3 w-3" />{l.viewers}</div>
                </div>
                <div className="p-4">
                  <h3 className="font-display font-bold line-clamp-1">{l.title}</h3>
                  <div className="text-xs text-slate-400 mt-1 flex items-center gap-2"><span>{l.seller_name}</span><VerifiedBadge verified={l.seller_verified} testId={`verified-${l.live_id}`} />{l.category && l.category !== 'autre' && <span className="text-slate-500">{catLabel(l.category)}</span>}</div>
                  {l.active_auction && <div className="mt-3 flex items-center justify-between p-2.5 rounded-xl bg-[#0b1120]/70 border border-white/5"><div className="min-w-0"><div className="eyebrow">Prix actuel</div><div className="font-mono-num gold-text font-bold">{fcfa(l.active_auction.current_price)}</div><div className="text-xs text-slate-300 truncate">{l.active_auction.product_name}</div></div><Radio className="h-4 w-4 text-[#E8B84A]" /></div>}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Final CTA */}
      {!user && (
        <section className="max-w-7xl mx-auto w-full px-4 sm:px-6 pb-16" data-testid="final-cta">
          <div className="glass-gold rounded-3xl p-8 sm:p-10 grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              <div className="eyebrow">Rejoindre JOTALY</div>
              <h2 className="mt-2 font-display text-2xl sm:text-3xl font-bold leading-tight">Ouvrez votre boutique aujourd hui. Vos premières clientes vous attendent.</h2>
              <p className="mt-2 text-slate-300 text-sm sm:text-base">Inscription gratuite par e-mail, téléphone ou Google. Boutique, produits et lives : tout est inclus.</p>
            </div>
            <div className="flex flex-col sm:flex-row md:flex-col gap-3">
              <Link to="/register" data-testid="cta-register-seller" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full btn-gold font-display font-bold">Devenir vendeur<ArrowRight className="h-4 w-4" /></Link>
              <Link to="/register" data-testid="cta-register-buyer" className="inline-flex items-center justify-center px-6 py-3.5 rounded-full btn-ghost-gold font-bold">Créer un compte acheteur</Link>
            </div>
          </div>
        </section>
      )}
      <Footer />
    </div>
  );
}
